/**
 * URL-encoded state for the Explore tab.
 *
 * Filters and column selection live in the query string so a view can be
 * bookmarked, reloaded, or pasted to someone else. App.tsx owns the path and
 * rewrites `location.search` on every route change, so it asks isExploreParam
 * which params to keep instead of dropping them (see writeRouteState).
 *
 * Multi-value filters use repeated params (?platform=ios&platform=tvos), never
 * comma-joined values: real device models look like "AppleTV11,1".
 */

/**
 * Filterable dimensions. The server's allowlist in
 * services/dashboard-api/src/lib/eventFilters.ts (FACET_DIMENSIONS) is the
 * source of truth; a key here that the server does not know is ignored there.
 */
export const DIMENSION_LABELS = {
  event_type: 'Event type',
  event_name: 'Event name',
  screen_name: 'Screen',
  platform: 'Platform',
  app_version: 'App version',
  build_number: 'Build',
  os_version: 'OS version',
  device_model: 'Device',
  country: 'Country',
  city: 'City',
  locale: 'Locale',
  timezone: 'Timezone',
  screen_resolution: 'Resolution',
  sdk_platform: 'SDK producer',
  sdk_version: 'SDK version',
  crash_reason: 'Crash reason',
} as const

export type Dimension = keyof typeof DIMENSION_LABELS

export const DIMENSIONS = Object.keys(DIMENSION_LABELS) as Dimension[]

/** Facets shown in the Explore panel, in display order. */
export const FACET_PANEL_DIMENSIONS: Dimension[] = [
  'event_type',
  'event_name',
  'screen_name',
  'app_version',
  'build_number',
  'os_version',
  'device_model',
  'country',
  'crash_reason',
]

export interface PropertyFilter {
  key: string
  /** Omitted to match on the key being present at all. */
  value?: string
}

export interface ExploreFilters {
  dimensions: Partial<Record<Dimension, string[]>>
  sessionId: string
  properties: PropertyFilter[]
}

export const EMPTY_EXPLORE_FILTERS: ExploreFilters = {
  dimensions: {},
  sessionId: '',
  properties: [],
}

const SESSION_PARAM = 'session'
const COLUMNS_PARAM = 'cols'
const PROPERTY_PARAM = 'prop'
const SPLIT_PARAM = 'split'
const METRIC_PARAM = 'metric'
// Property filters are encoded as "key" or "key:value". Splitting on the first
// colon lets values contain colons (URLs, timestamps); keys cannot, which holds
// for the snake_case keys both SDKs emit.
const PROPERTY_SEPARATOR = ':'

const DIMENSION_SET = new Set<string>(DIMENSIONS)

export function isDimension(value: string): value is Dimension {
  return DIMENSION_SET.has(value)
}

/** Whether a query-string key belongs to the Explore tab. */
export function isExploreParam(key: string): boolean {
  return (
    key === SESSION_PARAM ||
    key === COLUMNS_PARAM ||
    key === PROPERTY_PARAM ||
    key === SPLIT_PARAM ||
    key === METRIC_PARAM ||
    isDimension(key)
  )
}

export type ChartMetric = 'events' | 'sessions'

export interface ExploreChartSettings {
  metric: ChartMetric
  /** Empty for an unsplit chart. */
  splitBy: Dimension | ''
}

export const DEFAULT_CHART_SETTINGS: ExploreChartSettings = { metric: 'events', splitBy: '' }

export function readExploreChart(search: string = window.location.search): ExploreChartSettings {
  const params = new URLSearchParams(search)
  const split = params.get(SPLIT_PARAM) ?? ''
  return {
    metric: params.get(METRIC_PARAM) === 'sessions' ? 'sessions' : 'events',
    splitBy: isDimension(split) ? split : '',
  }
}

/** "app_foreground" -> "App Foreground". */
export function formatEventType(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

export function readExploreFilters(search: string = window.location.search): ExploreFilters {
  const params = new URLSearchParams(search)
  const dimensions: Partial<Record<Dimension, string[]>> = {}
  for (const dimension of DIMENSIONS) {
    const values = [...new Set(params.getAll(dimension).map(value => value.trim()).filter(Boolean))]
    if (values.length > 0) dimensions[dimension] = values
  }

  const properties: PropertyFilter[] = params.getAll(PROPERTY_PARAM).flatMap(raw => {
    const separator = raw.indexOf(PROPERTY_SEPARATOR)
    const key = (separator === -1 ? raw : raw.slice(0, separator)).trim()
    if (!key) return []
    if (separator === -1) return [{ key }]
    return [{ key, value: raw.slice(separator + 1) }]
  })

  return { dimensions, sessionId: params.get(SESSION_PARAM)?.trim() ?? '', properties }
}

/** Returns the column keys from the URL, or null when the URL does not pin a set. */
export function readExploreColumns(search: string = window.location.search): string[] | null {
  const raw = new URLSearchParams(search).get(COLUMNS_PARAM)
  if (!raw) return null
  const keys = raw.split(',').map(key => key.trim()).filter(Boolean)
  return keys.length > 0 ? keys : null
}

export function countActiveFilters(filters: ExploreFilters): number {
  const dimensionCount = Object.values(filters.dimensions).reduce((sum, values) => sum + (values?.length ?? 0), 0)
  return dimensionCount + filters.properties.length + (filters.sessionId ? 1 : 0)
}

export function hasActiveFilters(filters: ExploreFilters): boolean {
  return countActiveFilters(filters) > 0
}

/** Add or remove one value from a dimension filter. */
export function toggleDimensionValue(filters: ExploreFilters, dimension: Dimension, value: string): ExploreFilters {
  const current = filters.dimensions[dimension] ?? []
  const next = current.includes(value) ? current.filter(item => item !== value) : [...current, value]
  const dimensions = { ...filters.dimensions }
  if (next.length > 0) dimensions[dimension] = next
  else delete dimensions[dimension]
  return { ...filters, dimensions }
}

export function addPropertyFilter(filters: ExploreFilters, property: PropertyFilter): ExploreFilters {
  const exists = filters.properties.some(item => item.key === property.key && item.value === property.value)
  return exists ? filters : { ...filters, properties: [...filters.properties, property] }
}

export function removePropertyFilter(filters: ExploreFilters, index: number): ExploreFilters {
  return { ...filters, properties: filters.properties.filter((_, i) => i !== index) }
}

/** Stable string form of the filters, for use in query keys. */
export function filtersKey(filters: ExploreFilters): string {
  const dimensions = DIMENSIONS.flatMap(dimension => {
    const values = filters.dimensions[dimension]
    return values && values.length > 0 ? [[dimension, [...values].sort()]] : []
  })
  return JSON.stringify([dimensions, filters.sessionId, filters.properties])
}

/**
 * Push filter + column state into the URL without adding history entries --
 * refining a filter should not cost one Back press per change.
 */
export function writeExploreStateToUrl(
  filters: ExploreFilters,
  columnKeys: string[],
  defaultColumnKeys: string[],
  chart: ExploreChartSettings = DEFAULT_CHART_SETTINGS,
): void {
  const url = new URL(window.location.href)
  for (const key of [...url.searchParams.keys()]) {
    if (isExploreParam(key)) url.searchParams.delete(key)
  }

  for (const dimension of DIMENSIONS) {
    for (const value of filters.dimensions[dimension] ?? []) url.searchParams.append(dimension, value)
  }
  if (filters.sessionId) url.searchParams.set(SESSION_PARAM, filters.sessionId)
  for (const property of filters.properties) {
    url.searchParams.append(
      PROPERTY_PARAM,
      property.value === undefined ? property.key : `${property.key}${PROPERTY_SEPARATOR}${property.value}`,
    )
  }

  // Only pin columns when they differ from the default, to keep shared links short.
  const isDefault =
    columnKeys.length === defaultColumnKeys.length &&
    columnKeys.every((key, index) => key === defaultColumnKeys[index])
  if (!isDefault) url.searchParams.set(COLUMNS_PARAM, columnKeys.join(','))
  if (chart.metric !== DEFAULT_CHART_SETTINGS.metric) url.searchParams.set(METRIC_PARAM, chart.metric)
  if (chart.splitBy) url.searchParams.set(SPLIT_PARAM, chart.splitBy)

  window.history.replaceState(window.history.state, '', url)
}
